import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, Input, ContentChild, TemplateRef, OnDestroy } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ApiService } from '@core/api.service';
import { TranslateUiModule } from '@core/translation/translate-ui.module';
import { PushModule } from '@rx-angular/template/push';
import { EmptySearchModule } from '@shared/components/empty-search/empty-search.module';
import { LogoModule } from '@shared/components/logo/logo.module';
import { MultiselectOptionDirective } from '@ui-kit/app-multi-select/multiselect-option.directive';
import { LoaderModule } from '@ui-kit/loader/loader.module';
import { Collection } from 'collection';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'user-access-tab',
  templateUrl: './user-access-tab.component.html',
  styleUrls: ['./user-access-tab.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateUiModule,
    ReactiveFormsModule,
    FormsModule,
    PushModule,
    InfiniteScrollModule,
    LogoModule,
    LoaderModule,
    EmptySearchModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAccessTabComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() labelKey = 'full_name';

  @ContentChild(MultiselectOptionDirective, { static: false, read: TemplateRef }) optionTemplate;

  collection: Collection;

  destroyed$ = new Subject();

  inputSearch = new UntypedFormControl();
  isSelectAllChecked = false;
  _selected = new Set();

  clearWord = marker('Clear');

  clearSearch(): void {
    this.inputSearch.setValue('');
  }

  selectAll(state) {
    this.isSelectAllChecked = !state;

    if (this.isSelectAllChecked) {
      this.collection.selectAll();
      this._selected = this.collection.selectedItems;
    } else {
      this.collection.unSelectAll();
      this._selected = new Set();
    }

    this.getUserAccess().patchValue(Array.from(this._selected), { emitEvent: false });
  }

  showSearch(): boolean {
    return this.inputSearch.value && this.inputSearch.value.length > 0;
  }

  getOptions(op, key): string {
    return String(op[key]) || '';
  }

  reloadWorkers({ search, page }) {
    this.collection.setParams('page', page);
    search.length ? this.collection.setParams('search_query', search) : this.collection.clearParams('search_query');
    return Promise.resolve(this.collection.reload().toPromise());
  }

  onScroll() {
    if (!this.collection && !this.collection.loadByScrolling) {
      return;
    }

    if (this.collection.meta.currentPage === this.collection.meta.pageCount) {
      return;
    }

    if (!this.collection.loading && this.collection.loaded) {
      const search = this.inputSearch.value ? this.inputSearch.value : '';
      const page = this.collection.meta.currentPage + 1;
      this.reloadWorkers({ search, page }).then(() => {
        if (this.isSelectAllChecked) {
          this.collection.selectAll();
        }
      });
    }
  }

  handleSelect(option): void {
    if (this._selected.has(option.id)) {
      this._selected.delete(option.id);
      this.isSelectAllChecked = false;
    } else {
      this._selected.add(option.id);

      if (this.collection.meta.totalCount === this._selected.size) {
        this.isSelectAllChecked = true;
      }
    }

    this.getUserAccess().patchValue(Array.from(this._selected), { emitEvent: false });
  }

  private getUserAccess(): AbstractControl {
    return this.form.get('assigned_users');
  }

  private handlePreselectedWorkers() {
    this.getUserAccess().value.forEach((workerId) => {
      this._selected.add(workerId);
      return workerId;
    });

    if (this.getUserAccess().value.length === this.collection.meta.totalCount) {
      this.isSelectAllChecked = true;
    }
  }

  handlePreSelectAllWorkers(workers) {
    const workersIds = workers.map((worker) => {
      this._selected.add(worker.id);
      return worker.id;
    });
    this.getUserAccess().patchValue(workersIds, { emitEvent: false });
    this.isSelectAllChecked = true;
  }

  ngOnInit(): void {
    this.collection.reload().subscribe((workers) => {
      if (this.getUserAccess().value.length) {
        this.handlePreselectedWorkers();
      } else {
        this.handlePreSelectAllWorkers(workers);
      }
    });

    this.inputSearch.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((query: string) => {
      if (this.collection) {
        this.reloadWorkers({ search: query ? query : '', page: 1 });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  constructor(private _api: ApiService) {
    this.collection = new Collection({
      api: this._api.workers,
      params: {
        status: 'active',
        expand: 'full_name,avatars,wages',
        sort: 'first_name',
        per_page: -1,
        role: 'worker,manager'
      }
    });
    this.collection.loadByScrolling = true;
  }
}
